import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import thunk from 'redux-thunk';
import app, { createReducer } from './reducers';
import { historyMiddleware } from './Middlewares/History.middleware';
import MiniCart from './Components/Rutab/RutabMiniCart';
import RutabDesktopNavigation from './Components/Rutab/RutabDesktopNavigation';
import RutabMobileNavigation from './Components/Rutab/RutabMobileNavigation';
import RutabFacetedSearch from './Components/Rutab/RutabFacetedSearch';
import RutabFacetedSearchCompactContainer from './Components/Rutab/RutabFacetedSearchCompactContainer';
import DynamicComponent from './Components/DynamicComponent';
import { productTemplateTabsSetup } from './Actions/Rutab/ProductTemplateTabsSetup';
import CookieConsent from './Components/Rutab/CookieConsent';
import { CookiesProvider } from 'react-cookie';
import RutabQuickSearch from './Actions/Rutab/RutabQuickSearch';
import { preloadFonts } from './Editor/FontLoader';
import { ConfirmHost } from './Editor/Hooks/confirmProvider';

window.__litium = window.__litium || {};
const preloadState = window.__litium.preloadState || {};
const store = createStore(
  app,
  preloadState,
  composeWithDevTools(applyMiddleware(thunk, historyMiddleware))
);

// Add a dictionary to keep track of the registered async reducers
store.asyncReducers = {};

// Create an inject reducer function
// This function adds the async reducer, and creates a new combined reducer
store.injectReducer = (key, asyncReducer) => {
  if (!store.asyncReducers[key]) {
    store.asyncReducers[key] = asyncReducer;
    store.replaceReducer(createReducer(store.asyncReducers));
  }
};

window.__litium = {
  ...window.__litium,
  bootstrapComponents: () => {
    // bootstrap React components, in case the HTML response we receive from the server
    // has React components. ReactDOM.render performs only an update on previous rendered
    // components and only mutate the DOM as necessary to reflect latest React element.
    bootstrapComponents();
  },
  cache: {}, // for storing cache data for current request
};

const registeredContainers = [];
const renderReact = (element, container, callback) => {
  registeredContainers.push(container);
  ReactDOM.render(element, container, callback);
};

window.onunload = () => {
  // make sure components are unmounted, redux's listener are unsubscribed
  // to fix issue with iframe navigation in IE
  registeredContainers.forEach((container) => {
    ReactDOM.unmountComponentAtNode(container);
  });
};
const bootstrapComponents = () => {
  if (document.getElementById('miniCart')) {
    renderReact(
      <Provider store={store}>
        <MiniCart />
      </Provider>,
      document.getElementById('miniCart')
    );
  }
  if (document.getElementById('quickSearch')) {
    renderReact(
      <Provider store={store}>
        <RutabQuickSearch />
      </Provider>,
      document.getElementById('quickSearch')
    );
  }
  if (document.getElementById('desktopNavbar')) {
    renderReact(
      <Provider store={store}>
        <RutabDesktopNavigation />
      </Provider>,
      document.getElementById('desktopNavbar')
    );
  }
  if (document.getElementById('mobileNavbar')) {
    renderReact(
      <Provider store={store}>
        <RutabMobileNavigation />
      </Provider>,
      document.getElementById('mobileNavbar')
    );
  }
  if (document.getElementById('facetedSearch')) {
    renderReact(
      <Provider store={store}>
        <RutabFacetedSearch />
      </Provider>,
      document.getElementById('facetedSearch')
    );
  }
  if (document.getElementById('facetedSearchCompact')) {
    renderReact(
      <Provider store={store}>
        <RutabFacetedSearchCompactContainer />
      </Provider>,
      document.getElementById('facetedSearchCompact')
    );
  }
  if (document.getElementById('myPagePersons')) {
    const PersonList = DynamicComponent({
      loader: () => import('./Components/PersonListContainer'),
    });
    renderReact(
      <Provider store={store}>
        <PersonList />
      </Provider>,
      document.getElementById('myPagePersons')
    );
  }
  if (document.getElementById('myPageAddresses')) {
    const AddressList = DynamicComponent({
      loader: () => import('./Components/AddressListContainer'),
    });
    renderReact(
      <Provider store={store}>
        <AddressList />
      </Provider>,
      document.getElementById('myPageAddresses')
    );
  }
  if (document.getElementById('checkout')) {
    const Checkout = DynamicComponent({
      loader: () => import('./Components/Rutab/RutabCheckout'),
    });
    renderReact(
      <Provider store={store}>
        <Checkout />
      </Provider>,
      document.getElementById('checkout')
    );
  }
  if (document.getElementById('lightBoxImages')) {
    const LightboxImages = DynamicComponent({
      loader: () => import('./Components/LightboxImages'),
    });
    import('./Reducers/LightboxImages.reducer').then(({ lightboxImages }) => {
      store.injectReducer('lightboxImages', lightboxImages);
      const rootElement = document.getElementById('lightBoxImages');
      const images = Array.from(rootElement.querySelectorAll('template')).map(
        (img) => ({
          html: img.innerHTML,
          src: img.dataset.src,
        })
      );
      renderReact(
        <Provider store={store}>
          <LightboxImages images={images} />
        </Provider>,
        document.getElementById('lightBoxImages')
      );
    });
  }

  if (document.querySelectorAll('.slider').length > 0) {
    const Slider = DynamicComponent({
      loader: () => import('./Components/Slider'),
    });
    Array.from(document.querySelectorAll('.slider')).forEach(
      (slider, index) => {
        const values = [...slider.querySelectorAll('template')].map((block) => {
          return {
            html: block.innerHTML,
          };
        });
        if (values.length > 0) {
          renderReact(<Slider values={values} />, slider);
        }
      }
    );
  }

  if (document.querySelectorAll('.rutabdoubleslider').length > 0) {
    const RutabDoubleSlider = DynamicComponent({
      loader: () => import('./Components/RutabDoubleSlider'),
    });
    Array.from(document.querySelectorAll('.rutabdoubleslider')).forEach(
      (slider, index) => {
        const values = [...slider.querySelectorAll('template')].map((block) => {
          return {
            html: block.innerHTML,
          };
        });
        if (values.length > 0) {
          renderReact(<RutabDoubleSlider values={values} />, slider);
        }
      }
    );
  }

  if (document.querySelectorAll('buy-button').length > 0) {
    const BuyButton = DynamicComponent({
      loader: () => import('./Components/BuyButton'),
    });
    Array.from(document.querySelectorAll('buy-button')).forEach((button) => {
      const {
        articleNumber,
        quantityFieldId,
        href,
        cssClass,
        label,
      } = button.dataset;
      renderReact(
        <Provider store={store}>
          <BuyButton
            {...{
              label,
              articleNumber,
              quantityFieldId,
              href,
              cssClass,
            }}
          />
        </Provider>,
        button
      );
    });
  }
  if (document.getElementById('orderHistoryPage')) {
    const OrderList = DynamicComponent({
      loader: () => import('./Components/Rutab/RutabOrderHistoryListContainer'),
    });
    renderReact(
      <Provider store={store}>
        <OrderList />
      </Provider>,
      document.getElementById('orderHistoryPage')
    );
  }
  if (document.getElementById('variantFilter')) {
    const VariantFilter = DynamicComponent({
      loader: () => import('./Components/Rutab/RutabVariantFilter'),
    });
    const baseProductSystemId = document.getElementById('variantFilter').dataset
      .baseproductsystemid;
    renderReact(
      <Provider store={store}>
        <VariantFilter baseProductSystemId={baseProductSystemId} />
      </Provider>,
      document.getElementById('variantFilter')
    );
  }

  if (document.querySelectorAll('.productTabs').length > 0) {
    productTemplateTabsSetup();
  }

  if (document.getElementById('cookie-consent-id')) {
    renderReact(
      <CookiesProvider>
        <CookieConsent />
      </CookiesProvider>,
      document.getElementById('cookie-consent-id')
    );
  }

  if (document.querySelectorAll('.media-folder-explorer').length > 0) {
    const MediaFolderExplorer = DynamicComponent({
      loader: () => import('./Components/Rutab/RutabMediaFolderExplorer'),
    });
    Array.from(document.querySelectorAll('.media-folder-explorer')).forEach(
      (mediaFolderExplorer, index) => {
        const id = mediaFolderExplorer.dataset.rootfoldersystemid;
        const blockSystemId = mediaFolderExplorer.dataset.blocksystemid;

        renderReact(
          <Provider store={store}>
            <MediaFolderExplorer
              rootfolderId={id}
              blockSystemId={blockSystemId}
            />
          </Provider>,
          mediaFolderExplorer
        );
      }
    );
  }

  if (document.getElementById('decal--editor')) {
    const breadcrumbsContainer = document.querySelector('#breadcrumbs');
    if (breadcrumbsContainer) {
      breadcrumbsContainer.style.display = 'none';
    }
    const DecalEditor = DynamicComponent({
      loader: () => import('./Components/Rutab/DecalEditor'),
    });
    renderReact(
      <Provider store={store}>
        <ConfirmHost>
          <DecalEditor />
        </ConfirmHost>
      </Provider>,
      document.getElementById('decal--editor')
    );
  }
};

// Call preloader when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', preloadFonts);
} else {
  preloadFonts();
}

bootstrapComponents();
